import React, { useState, useEffect } from 'react';
import { Home, Search, Users, Send, CheckCircle } from 'lucide-react';
import { householdAPI } from '../services/api';
import '../styles/Modal.css';
import '../styles/BrowseHouseholds.css';

function BrowseHouseholdsModal({ isOpen, onClose, onRequestSent }) {
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingRequest, setSendingRequest] = useState(null);
  const [sentRequests, setSentRequests] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadHouseholds();
    }
  }, [isOpen]);

  const loadHouseholds = async () => {
    setLoading(true);
    try {
      const response = await householdAPI.getPublic();
      setHouseholds(response.data);
    } catch (error) {
      console.error('Failed to load households:', error);
      setHouseholds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (householdId) => {
    setSendingRequest(householdId);
    try {
      await householdAPI.sendJoinRequest(householdId);
      setSentRequests([...sentRequests, householdId]);
      if (onRequestSent) {
        onRequestSent(householdId);
      }
    } catch (error) {
      console.error('Failed to send join request:', error);
      alert(error.response?.data?.error || 'Failed to send join request');
    } finally {
      setSendingRequest(null);
    }
  };

  const filteredHouseholds = households.filter(household =>
    household.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container browse-households-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-wrapper">
            <Home size={24} />
            <h3>Browse Households</h3>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p className="browse-description">
            Find and request to join an existing household. A manager will review your request.
          </p>
          
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search households..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading households...</p>
            </div>
          ) : filteredHouseholds.length === 0 ? (
            <div className="empty-state">
              <Home size={48} />
              <h3>No households found</h3>
              <p>
                {searchTerm 
                  ? 'Try adjusting your search or create your own household'
                  : 'There are no other households available to join at this time.'}
              </p>
            </div>
          ) : (
            <div className="households-list">
              {filteredHouseholds.map(household => {
                const hasRequested = sentRequests.includes(household.id);
                const isSending = sendingRequest === household.id;

                return (
                  <div key={household.id} className="household-card">
                    <div className="household-card-icon">
                      <Home size={24} />
                    </div>
                    <div className="household-card-info">
                      <h4 className="household-card-name">{household.name}</h4>
                      <div className="household-card-meta">
                        <span className="household-card-meta-item">
                          <Users size={12} />
                          {household.member_count} {household.member_count === 1 ? 'member' : 'members'}
                        </span>
                        <span>•</span>
                        <span>Created {new Date(household.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="household-card-action">
                      {hasRequested ? (
                        <button className="btn-success-outline" disabled>
                          <CheckCircle size={16} />
                          Sent
                        </button>
                      ) : (
                        <button
                          className="btn-primary"
                          onClick={() => handleSendRequest(household.id)}
                          disabled={isSending}
                        >
                          <Send size={16} />
                          {isSending ? 'Sending...' : 'Request to Join'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BrowseHouseholdsModal;
